import React from 'react';
import { useTranslation } from 'react-i18next';
import CustomDayPreview from '../../../../../../../components/CustomDayPreview';
import {
    BONUS_TYPE_ENUM,
    POLICY_CONDITION_MODEL
} from '../../../OvertimePolicies/Global/CreateUpdatePolicy/Model/Model';
import { getDateFormat, getTimeFormat } from '../../../../../../../Apis/functions';
import { convertToDate } from '../../../../../../../utils/TimeConversion';

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 *
 * Scope rows of the overtime breakdown modal. Each policy condition stores its
 * scope in a different snapshot object, so the rendered rows depend on it.
 *
 * @param {Object} item - Overtime line snapshot holding the policy scopes.
 */

// Label/value row matching the rest of the breakdown modal rows.
const ScopeRow = ({ label, value }) => (
    <p className='fs-14px mb-1'><span className='ibl-font-w-600'>{label}:</span> {value}</p>
);

const BreakdownScopeDetail = ({ item }) => {
    const { t } = useTranslation();

    const condition = item?.policyConditionSnapshot;

    if (condition === POLICY_CONDITION_MODEL.WEEKLY_RECURRENCE.key) {
        const weekly = item?.payrollPolicyWeeklyScope ?? [];
        if (!weekly.length) return null;

        const { startTime, endTime } = weekly[0] ?? {};

        return (
            <>
                <div className='d-flex align-items-center mb-1'>
                    <span className='fs-14px ibl-font-w-600 mr-8px'>{t('days')}:</span>
                    <CustomDayPreview
                        id='cb_obm_scope_days'
                        dayArray={weekly.map((day) => day?.dayId)}
                    />
                </div>
                <ScopeRow
                    label={t('schedule')}
                    value={`${getTimeFormat(convertToDate(startTime))} ${t('to').toLowerCase()} ${getTimeFormat(convertToDate(endTime))}`}
                />
            </>
        );
    }

    if (condition === POLICY_CONDITION_MODEL.DATE_RANGE.key) {
        const dateScope = item?.payrollPolicyDateScope;
        if (!dateScope) return null;

        const { startDate, endDate } = dateScope;
        // A single day policy stores the same date on both ends.
        const value = startDate === endDate
            ? getDateFormat(startDate, false)
            : `${getDateFormat(startDate, false)} ${t('to').toLowerCase()} ${getDateFormat(endDate, false)}`;

        return <ScopeRow label={t('date_range')} value={value} />;
    }

    if (condition === POLICY_CONDITION_MODEL.HOUR.key) {
        const hourScope = item?.payrollPolicyHourScope;
        if (!hourScope) return null;

        const { minValue, maxValue } = hourScope;
        const value = minValue === maxValue
            ? `${minValue}${t('h')}`
            : `${minValue}${t('h')} ${t('to').toLowerCase()} ${maxValue}${t('h')}`;

        return <ScopeRow label={t('extra_hours')} value={value} />;
    }

    if (condition === POLICY_CONDITION_MODEL.BONUS.key) {
        const bonusScope = item?.payrollPolicyBonusScope;
        if (!bonusScope) return null;

        const bonusType = BONUS_TYPE_ENUM[bonusScope.bonusType];

        return (
            <>
                <ScopeRow label={t('bonus_type')} value={t(bonusType?.trans)} />
                <ScopeRow label={t('threshold')} value={`${bonusScope.value}${bonusType?.prefixText ?? ''}`} />
            </>
        );
    }

    return null;
};

export default BreakdownScopeDetail;
